import { memo } from 'react';
import { useTranslation } from 'next-i18next';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

export interface ExternalResourceCardProps {
  /** Title of the linked resource, as shown to the user. */
  title: string;
  /** Absolute URL of the resource. The card renders nothing when this is empty. */
  href?: string;
  /**
   * Call-to-action label. Defaults to "Leggi l'articolo", which only fits blog
   * posts: pass something else when the destination is a tool or a guide hub.
   */
  ctaLabel?: string;
}

/**
 * Card linking to a resource outside the app (a blog article, a salary page, a
 * calculator on the marketing site). Opens in a new tab so the app stays open
 * behind it.
 *
 * The whole card is the anchor, not just the bottom row: it is the primary
 * surface on mobile, where a text-sized tap target is too small.
 *
 * Plain anchor rather than a design-system component, on purpose. `LinkBase`
 * (@restworld/ui-ds) is deprecated, and `Link` (@restworld/ui-ds-base) is not a
 * drop-in replacement here: it calls preventDefault() and then window.open()
 * behind a 200ms timeout, which lands outside the user-gesture window and can
 * be stopped by a popup blocker. For a card whose only job is to open a link,
 * native anchor navigation is the safer behaviour. Per root CLAUDE.md, plain
 * styled HTML is preferred over reaching back into the deprecated lib.
 *
 * Not in `ui-ds-base` on purpose: it is a product pattern, not a UI primitive.
 * A twin copy will live in employer-app; both should move to the shared
 * component library once the design system rework creates it.
 */
export const ExternalResourceCard = memo(({ title, href, ctaLabel }: ExternalResourceCardProps) => {
  const { t } = useTranslation();

  if (!href) return null;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="w-full p-4 bg-purple-50 border border-purple-800 rounded-[15px] flex flex-col justify-end items-start gap-2 no-underline cursor-pointer"
    >
      <span className="self-stretch text-gray-900 text-base font-semibold leading-normal">{title}</span>
      <span className="justify-center items-center gap-1.5 inline-flex">
        <span className="text-primary-800 text-sm font-medium leading-[21px]">
          {ctaLabel ?? t('job-application-page:read_the_article')}
        </span>
        <ArrowTopRightOnSquareIcon className="w-5 h-5 relative text-primary-800" />
      </span>
    </a>
  );
});

ExternalResourceCard.displayName = 'ExternalResourceCard';

export default ExternalResourceCard;
